import { createContext, useContext, useState, useEffect, useCallback } from 'react';

let logoutTimer;

const TOKEN_DURATION = 1000 * 60 * 60 * 24 * 14; // 14 days
const REAUTH_DURATION = 1000 * 60 * 60 * 24; // 24 hours

const AuthContext = createContext({
  isLoggedIn: false,
  userId: null,
  token: null,
  login: () => {},
  logout: () => {},
  isJesseAcct: false,
  isPrivateUnlocked: false,
  unlockPrivate: () => {},
  lockPrivate: () => {},
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [tokenExpirationDate, setTokenExpirationDate] = useState();
  const [isJesseAcct, setIsJesseAcct] = useState(false);
  const [isPrivateUnlocked, setIsPrivateUnlocked] = useState(false);

  const login = useCallback((id, token, expirationDate) => {
    setToken(token);
    setUserId(id);

    const tokenExpiration = expirationDate || new Date(Date.now() + TOKEN_DURATION);

    setTokenExpirationDate(tokenExpiration);

    localStorage.setItem(
      'userData',
      JSON.stringify({
        userId: id,
        token,
        expiration: tokenExpiration.toISOString(),
        lastLogin: new Date().toISOString()
      })
    );
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setTokenExpirationDate(null);
    setUserId(null);
    setIsPrivateUnlocked(false);

    localStorage.removeItem('userData');
  }, []);

  const unlockPrivate = useCallback(() => {
    setIsPrivateUnlocked(true);
  }, []);

  const lockPrivate = useCallback(() => {
    setIsPrivateUnlocked(false);
  }, []);

  useEffect(() => {
    if (token && tokenExpirationDate) {
      const remainingTime = tokenExpirationDate.getTime() - Date.now();
      logoutTimer = setTimeout(logout, remainingTime);
    } else {
      clearTimeout(logoutTimer);
    }

    return () => clearTimeout(logoutTimer);
  }, [token, logout, tokenExpirationDate]);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('userData'));

    if (!storedData?.token) return;

    const lastLogin = new Date(storedData.lastLogin);
    const reauthExpired = Date.now() - lastLogin.getTime() > REAUTH_DURATION;
    const tokenExpired = new Date(storedData.expiration) <= new Date();

    if (!reauthExpired && !tokenExpired) {
      login(
        storedData.userId,
        storedData.token,
        new Date(storedData.expiration)
      );
    } else {
      logout();
    }
  }, [login, logout]);

  useEffect(() => {
    setIsJesseAcct(userId === process.env.REACT_APP_JESSE_ID);
  }, [userId]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        token,
        userId,
        login,
        logout,
        isJesseAcct,
        isPrivateUnlocked,
        unlockPrivate,
        lockPrivate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);